import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import {
  WeekCalendarComponent,
  DiaSemana
} from '../agenda/administrar-agenda/components/week-calendar/week-calendar.component';
import { AgendaDesktopV2Component } from '../agenda/administrar-agenda/components/agenda-desktop-v2/agenda-desktop-v2.component';
import {
  AGENDA_DIALOG_CONTENT_STYLE_V2,
  AGENDA_DIALOG_STYLE_V2,
  AgendaDesktopV2Service
} from '../agenda/administrar-agenda/services/agenda-desktop-v2.service';
import {
  CitaDialogComponent,
  CitaMode
} from '../agenda/cita/container/cita-dialog.component';
import { CancelarCitaResult } from '../agenda/cita/components/cancelar-cita-dialog.component';
import {
  TipoCitaSelectorDialogComponent,
  UsoCitaTipo
} from './tipo-cita-selector-dialog.component';
import { AgendaRepository } from 'src/app/domain/service/agenda/agenda-repository';
import {
  ProgramacionServicio,
  TipoCita,
  ProgramacionServicioRequest
} from 'src/app/domain/model/agenda/agenda';
import { OrdenServicioDetalleProgramar } from 'src/app/domain/model/orden-servicio/orden-servicio';
import { ESTADO_CITA } from 'src/app/common/constants-ejb';
import { extractHttpErrorMessage } from 'src/app/common/http-error-utils';

/**
 * AgendaAsesorDialogComponent
 *
 * Diálogo que muestra el calendario semanal del asesor para programar citas
 * desde el flujo de "Programar Servicio" de órdenes de servicio.
 *
 * Características:
 * - Muestra el calendario semanal con las citas existentes del asesor
 * - Permite navegar entre semanas
 * - Al hacer clic en una celda vacía, abre CitaDialogComponent en modo create
 * - Pasa la información de la orden de servicio para pre-cargar datos
 * - Solo permite crear citas tipo CAMPO (17) o INFORME (18)
 *
 * Legacy: SchedulingServicesCalendarActivity.java
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-agenda-asesor-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    DropdownModule,
    ProgressSpinnerModule,
    WeekCalendarComponent,
    AgendaDesktopV2Component,
    CitaDialogComponent,
    TipoCitaSelectorDialogComponent
  ],
  template: `
    <p-dialog
      [(visible)]="dialogVisible"
      [header]="'Agenda del Consultor - ' + (nombreAsesor() || dniAsesor())"
      [modal]="true"
      [style]="agendaV2() ? dialogStyleV2 : dialogStyle"
        [styleClass]="agendaV2() ? 'agenda-v2-dialog' : ''"
      [contentStyle]="agendaV2() ? dialogContentStyleV2 : dialogContentStyle"
      [breakpoints]="agendaV2() ? {} : dialogBreakpoints"
      [closable]="true"
      (onHide)="cerrar()"
    >
      @if (cargando()) {
        <div class="flex justify-center items-center py-8">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      } @else {
        <!-- Información de la orden (resumen) -->
        <div
          class="rounded border border-blue-200"
          [ngClass]="agendaV2() ? 'mb-2 px-3 py-1' : 'mb-4 p-3'"
        >
          <div
            class="flex flex-wrap text-sm"
            [ngClass]="agendaV2() ? 'gap-x-4 gap-y-0' : 'gap-4'"
          >
            <div>
              <span class="text-gray-500">Orden:</span>
              <span class="ml-1 font-medium">{{
                ordenServicioDetalle()?.codigoOrden ||
                  ordenServicioDetalle()?.nmconsecutivo
              }}</span>
            </div>
            <div>
              <span class="text-gray-500">Cliente:</span>
              <span class="ml-1 font-medium">{{
                ordenServicioDetalle()?.nombreBeneficiario || '-'
              }}</span>
            </div>
            <div>
              <span class="text-gray-500">Tarea:</span>
              <span class="ml-1 font-medium">{{
                ordenServicioDetalle()?.tareaCliente || '-'
              }}</span>
            </div>
            <div>
              <span class="text-gray-500">Saldo:</span>
              <span
                class="ml-1 font-medium"
                [class.text-red-600]="
                  (ordenServicioDetalle()?.saldoPorProgramar || 0) > 0
                "
              >
                {{ ordenServicioDetalle()?.saldoPorProgramar || 0 }}
              </span>
            </div>
          </div>
        </div>

        @if (agendaV2()) {
          <div class="flex-1 min-h-0">
            <app-agenda-desktop-v2
              [anio]="anioSeleccionado()"
              [mes]="mesSeleccionado()"
              [anios]="anios"
              [meses]="meses"
              [nombreMes]="nombreMes()"
              [diasSemanaCortos]="diasSemanaCortos"
              [diasVaciosInicio]="diasVaciosInicio()"
              [diasDelMes]="diasDelMes()"
              [leyenda]="tiposCitaLeyenda"
              [esDiaMarcado]="esDiaMarcadoFn"
              [esHoy]="esHoyFn"
              [citas]="citasSemana()"
              [diasSemana]="diasSemana()"
              [loading]="cargandoCitas()"
              (anioChange)="onAnioChange($event)"
              (mesChange)="onMesChange($event)"
              (mesAnterior)="mesAnterior()"
              (mesSiguiente)="mesSiguiente()"
              (diaClick)="seleccionarSemana($event)"
              (semanaAnterior)="irSemanaAnterior()"
              (semanaSiguiente)="irSemanaSiguiente()"
              (hoy)="irSemanaHoy()"
              (citaClick)="onCitaClick($event)"
              (celdaClick)="onCeldaClick($event)"
            />
          </div>
        } @else {
        <!-- Layout de 2 columnas: Calendario mensual + Semanal -->
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <!-- Panel Izquierdo: Selector Año/Mes + Calendario Mensual + Leyenda -->
          <div class="lg:col-span-1">
            <!-- Selector Año/Mes -->
            <div class="flex gap-2 mb-3">
              <div class="flex-1">
                <span class="block text-sm font-medium text-gray-700 mb-1"
                  >Año</span
                >
                <p-dropdown
                  [ngModel]="anioSeleccionado()"
                  (ngModelChange)="onAnioChange($event)"
                  [options]="anios"
                  appendTo="body"
                  [style]="{ width: '100%' }"
                >
                </p-dropdown>
              </div>
              <div class="flex-1">
                <span class="block text-sm font-medium text-gray-700 mb-1"
                  >Mes</span
                >
                <p-dropdown
                  [ngModel]="mesSeleccionado()"
                  (ngModelChange)="onMesChange($event)"
                  [options]="meses"
                  optionLabel="label"
                  optionValue="value"
                  appendTo="body"
                  [style]="{ width: '100%' }"
                >
                </p-dropdown>
              </div>
            </div>

            <!-- Mini Calendario Mensual -->
            <div class="border rounded-lg p-2 mb-4">
              <div class="flex justify-between items-center mb-2">
                <button
                  pButton
                  icon="pi pi-chevron-left"
                  class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                  aria-label="Mes anterior"
                  (click)="mesAnterior()"
                ></button>
                <span class="font-semibold text-sm"
                  >{{ nombreMes() }} {{ anioSeleccionado() }}</span
                >
                <button
                  pButton
                  icon="pi pi-chevron-right"
                  class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                  aria-label="Mes siguiente"
                  (click)="mesSiguiente()"
                ></button>
              </div>
              <div class="grid grid-cols-7 gap-0 text-center text-xs">
                @for (dia of diasSemanaCortos; track dia) {
                  <div class="p-1 sm:p-2 font-medium text-gray-600">
                    {{ dia }}
                  </div>
                }
                @for (empty of diasVaciosInicio(); track $index) {
                  <div class="p-1 sm:p-2"></div>
                }
                @for (dia of diasDelMes(); track dia) {
                  <div
                    class="p-1 sm:p-2 min-h-[32px] sm:min-h-[36px] cursor-pointer rounded hover:bg-blue-100 text-center flex items-center justify-center"
                    [class.bg-blue-500]="esDiaEnSemanaSeleccionada(dia)"
                    [class.text-white]="esDiaEnSemanaSeleccionada(dia)"
                    [class.font-bold]="esHoy(dia)"
                    (click)="seleccionarSemana(dia)"
                  >
                    {{ dia }}
                  </div>
                }
              </div>
            </div>

            <!-- Leyenda de colores -->
            <div class="border rounded-lg p-3">
              <h4 class="font-semibold text-sm mb-2">Leyenda</h4>
              <div class="flex flex-col gap-1 text-xs">
                @for (tipo of tiposCitaLeyenda; track (tipo.label || $index)) {
                  <div class="flex items-center gap-2">
                    <span
                      class="w-4 h-4 rounded"
                      [style.background-color]="tipo.color"
                    ></span>
                    <span>{{ tipo.label }}</span>
                  </div>
                }
              </div>
            </div>
          </div>

          <!-- Panel Derecho: Calendario Semanal -->
          <div class="lg:col-span-3">
            <app-week-calendar
              [citas]="citasSemana()"
              [diasSemana]="diasSemana()"
              [loading]="cargandoCitas()"
              (citaClick)="onCitaClick($event)"
              (celdaClick)="onCeldaClick($event)"
              (semanaAnterior)="irSemanaAnterior()"
              (semanaSiguiente)="irSemanaSiguiente()"
            >
            </app-week-calendar>
          </div>
        </div>
        }
      }

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="cerrar()"
          >
          </p-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Diálogo de crear cita -->
    @if (mostrarCrearCita()) {
      <app-cita-dialog
        [cita]="citaNueva()"
        [mode]="'create'"
        [tiposCita]="tiposCita()"
        [ordenServicioDetalle]="ordenServicioDetalleParaCita()"
        (onClose)="cerrarCrearCita()"
        (onSave)="onGuardarCita($event)"
      >
      </app-cita-dialog>
    }

    <!-- Diálogo de ver/editar cita existente -->
    @if (mostrarVerCita()) {
      <app-cita-dialog
        [cita]="citaSeleccionada()"
        [mode]="modoCita()"
        [tiposCita]="tiposCitaCompletos()"
        [ordenServicioDetalle]="ordenServicioDetalle()"
        (onClose)="cerrarVerCita()"
        (onSave)="onActualizarCita($event)"
        (onCancel)="onCancelarCita($event)"
        (onCumplir)="onCumplirCita($event)"
        (onCumplimientoRegistrado)="onCumplimientoRegistrado($event)"
      >
      </app-cita-dialog>
    }

    <!-- Diálogo de selección de tipo de cita -->
    @if (mostrarSelectorTipoCita()) {
      <app-tipo-cita-selector-dialog
        [visible]="mostrarSelectorTipoCita()"
        (seleccionado)="onTipoCitaSeleccionado($event)"
        (cancelado)="onTipoCitaCancelado()"
      >
      </app-tipo-cita-selector-dialog>
    }
  `
})
export class AgendaAsesorDialogComponent {
  // === Inputs ===
  visible = input.required<boolean>();
  ordenServicioDetalle = input.required<OrdenServicioDetalleProgramar | null>();
  dniAsesor = input.required<string>();
  nmconsecutivoAsesor = input<number | null>(null);
  nombreAsesor = input<string>('');

  // === Outputs ===
  closed = output<void>();
  citaCreada = output<ProgramacionServicio>();

  // === Services ===
  private readonly agendaRepository = inject(AgendaRepository);
  private readonly messageService = inject(MessageService);

  // === Agenda desktop v2 (>= 1024 px) ===
  private readonly agendaDesktopV2 = inject(AgendaDesktopV2Service);
  readonly agendaV2 = this.agendaDesktopV2.habilitado;
  readonly esDiaMarcadoFn = (dia: number): boolean =>
    this.esDiaEnSemanaSeleccionada(dia);
  readonly esHoyFn = (dia: number): boolean => this.esHoy(dia);

  readonly dialogStyle = { width: '92vw', maxWidth: '88rem' };
  readonly dialogContentStyle = { 'max-height': '80vh', 'overflow-y': 'auto' };
  readonly dialogBreakpoints = {
    '1280px': '92vw',
    '1024px': '94vw',
    '768px': '96vw',
    '640px': '98vw'
  };
  readonly dialogStyleV2 = AGENDA_DIALOG_STYLE_V2;
  readonly dialogContentStyleV2 = AGENDA_DIALOG_CONTENT_STYLE_V2;

  // === State ===
  dialogVisible = false;
  cargando = signal(false);
  cargandoCitas = signal(false);
  private isInitializing = false; // Flag para evitar ciclo infinito en onMesAnioChange

  // Semana actual
  fechaSemanaActual = signal(new Date());
  citasSemana = signal<ProgramacionServicio[]>([]);

  // Valores de asesor capturados para evitar problemas de timing
  private asesorDni = signal<string>('');
  private asesorNmconsec = signal<number | null>(null);

  // Tipos de cita
  tiposCita = signal<TipoCita[]>([]); // Solo CAMPO/INFORME para órdenes
  tiposCitaCompletos = signal<TipoCita[]>([]); // Todos los tipos (para ver citas existentes)

  // Diálogo crear cita
  mostrarCrearCita = signal(false);
  citaNueva = signal<ProgramacionServicio | null>(null);

  // Diálogo ver/editar cita
  mostrarVerCita = signal(false);
  citaSeleccionada = signal<ProgramacionServicio | null>(null);
  modoCita = signal<CitaMode>('view');

  // Diálogo selector de tipo de cita (Administrativas vs Campo/Informe)
  mostrarSelectorTipoCita = signal(false);
  private celdaClickPendiente = signal<{
    fecha: string;
    hora: number;
    minuto?: number;
  } | null>(null);
  private tipoCitaSeleccionadoParaCrear = signal<UsoCitaTipo | null>(null);

  // === Calendario Mensual ===
  anioSeleccionado = signal(new Date().getFullYear());
  mesSeleccionado = signal(new Date().getMonth() + 1);

  // Opciones para dropdowns
  anios = Array.from(
    { length: 10 },
    (_, i) => new Date().getFullYear() - 5 + i
  );
  meses = [
    { label: 'ENERO', value: 1 },
    { label: 'FEBRERO', value: 2 },
    { label: 'MARZO', value: 3 },
    { label: 'ABRIL', value: 4 },
    { label: 'MAYO', value: 5 },
    { label: 'JUNIO', value: 6 },
    { label: 'JULIO', value: 7 },
    { label: 'AGOSTO', value: 8 },
    { label: 'SEPTIEMBRE', value: 9 },
    { label: 'OCTUBRE', value: 10 },
    { label: 'NOVIEMBRE', value: 11 },
    { label: 'DICIEMBRE', value: 12 }
  ];

  diasSemanaCortos = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

  // Leyenda de colores del legacy
  tiposCitaLeyenda = [
    { label: 'Campo', color: '#31B404' },
    { label: 'Informe', color: '#9F81F7' },
    { label: 'Cita Tentativa', color: '#F87431' },
    { label: 'Bloqueo Program', color: '#ADD8E6' },
    { label: 'Administrativas', color: '#F4FA58' },
    { label: 'Cliente Particular', color: '#1589FF' },
    { label: 'Bloqueo Adscritos', color: '#736F6E' },
    { label: 'EPT Salud', color: '#31B404' }
  ];

  // === Computed ===

  /**
   * Retorna el detalle de la orden de servicio solo si el usuario seleccionó
   * tipo de cita "SERVICIO" (Campo/Informe). Para citas administrativas retorna null.
   */
  ordenServicioDetalleParaCita = computed(() => {
    return this.tipoCitaSeleccionadoParaCrear() === 'SERVICIO'
      ? this.ordenServicioDetalle()
      : null;
  });

  diasSemana = computed<DiaSemana[]>(() => {
    const fecha = this.fechaSemanaActual();
    const dias: DiaSemana[] = [];

    // Obtener el domingo de la semana actual (como en Administrar Agenda)
    const domingo = new Date(fecha);
    const dia = domingo.getDay();
    domingo.setDate(domingo.getDate() - dia); // Retrocede al domingo

    const nombresDias = [
      'Domingo',
      'Lunes',
      'Martes',
      'Miércoles',
      'Jueves',
      'Viernes',
      'Sábado'
    ];
    const meses = [
      'Ene',
      'Feb',
      'Mar',
      'Abr',
      'May',
      'Jun',
      'Jul',
      'Ago',
      'Sep',
      'Oct',
      'Nov',
      'Dic'
    ];

    for (let i = 0; i < 7; i++) {
      const d = new Date(domingo);
      d.setDate(domingo.getDate() + i);
      // Usar formato local YYYY-MM-DD (no toISOString que convierte a UTC)
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      dias.push({
        fecha: `${year}-${month}-${day}`,
        nombreDia: nombresDias[i],
        fechaCorta: `${d.getDate()} ${meses[d.getMonth()]}`
      });
    }

    return dias;
  });

  // Computed para calendario mensual
  nombreMes = computed(() => {
    const nombres = [
      '',
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre'
    ];
    return nombres[this.mesSeleccionado()];
  });

  diasVaciosInicio = computed((): number[] => {
    const primerDia = new Date(
      this.anioSeleccionado(),
      this.mesSeleccionado() - 1,
      1
    );
    return Array<number>(primerDia.getDay()).fill(0);
  });

  diasDelMes = computed(() => {
    const ultimoDia = new Date(
      this.anioSeleccionado(),
      this.mesSeleccionado(),
      0
    ).getDate();
    return Array.from({ length: ultimoDia }, (_, i) => i + 1);
  });

  // === Effects ===
  private visibilityEffect = effect(() => {
    const isVisible = this.visible();
    const dni = this.dniAsesor();
    const nmconsec = this.nmconsecutivoAsesor();

    // Necesitamos al menos uno de los dos identificadores
    if (isVisible && (dni || nmconsec)) {
      // Marcar que estamos inicializando ANTES de cualquier cambio
      this.isInitializing = true;

      // Capturar valores para evitar problemas de timing
      this.asesorDni.set(dni);
      this.asesorNmconsec.set(nmconsec);

      // Resetear a la semana actual ANTES de mostrar el diálogo
      const hoy = new Date();
      this.fechaSemanaActual.set(hoy);
      this.anioSeleccionado.set(hoy.getFullYear());
      this.mesSeleccionado.set(hoy.getMonth() + 1);

      this.dialogVisible = true;
      void this.cargarDatosIniciales().finally(() => {
        // Quitar el flag DESPUÉS de cargar los datos
        this.isInitializing = false;
      });
    } else if (!isVisible) {
      this.dialogVisible = false;
      this.limpiar();
    }
  });

  // === Métodos de carga ===

  private async cargarDatosIniciales(): Promise<void> {
    this.cargando.set(true);

    try {
      // Cargar tipos de cita (para el dropdown en crear cita)
      await this.cargarTiposCita();

      // Cargar citas de la semana actual
      await this.cargarCitasSemana();
    } catch (error) {
      console.error('[AgendaAsesor] Error cargando datos:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cargar la agenda del consultor'
      });
    } finally {
      this.cargando.set(false);
    }
  }

  private async cargarTiposCita(): Promise<void> {
    try {
      // Cargar todos los tipos (ADMIN y SERVICIO) para poder ver citas existentes
      const [tiposAdmin, tiposServicio] = await Promise.all([
        this.agendaRepository.listarTiposCita(this.dniAsesor(), 'ADMIN'),
        this.agendaRepository.listarTiposCita(this.dniAsesor(), 'SERVICIO')
      ]);

      // Combinar todos los tipos para ver citas existentes
      const todosLosTipos = [...tiposAdmin, ...tiposServicio];
      this.tiposCitaCompletos.set(todosLosTipos);

      // tiposCita se cargará dinámicamente cuando el usuario seleccione
      // Administrativas o Campo/Informe en el selector
    } catch (error) {
      console.error('[AgendaAsesor] Error cargando tipos de cita:', error);
    }
  }

  private async cargarCitasSemana(): Promise<void> {
    console.log('[AgendaAsesor] cargarCitasSemana() iniciando...');
    this.cargandoCitas.set(true);

    try {
      const dias = this.diasSemana();
      if (dias.length === 0) {
        console.log('[AgendaAsesor] cargarCitasSemana - No hay días, saliendo');
        return;
      }

      const fechaInicio = dias[0].fecha;
      const fechaFin = dias[dias.length - 1].fecha;

      // Usar los valores capturados en el effect para evitar problemas de timing
      const dni = this.asesorDni();
      const nmconsec = this.asesorNmconsec();

      console.log('[AgendaAsesor] cargarCitasSemana - nmconsec:', nmconsec);

      let citas: ProgramacionServicio[];

      // Usar DNI si está disponible, si no usar nmconsecutivoAsesor
      if (dni) {
        citas = await this.agendaRepository.consultarCitasRangoByDni(
          dni,
          fechaInicio,
          fechaFin
        );
      } else if (nmconsec) {
        citas = await this.agendaRepository.consultarCitasRango(
          nmconsec,
          fechaInicio,
          fechaFin
        );
      } else {
        citas = [];
      }

      this.citasSemana.set(citas);
      console.log(
        '[AgendaAsesor] cargarCitasSemana - Citas cargadas:',
        citas.length
      );
    } catch (error) {
      console.error('[AgendaAsesor] Error cargando citas:', error);
      this.citasSemana.set([]);
    } finally {
      this.cargandoCitas.set(false);
    }
  }

  // === Navegación de semana ===

  irSemanaAnterior(): void {
    const fecha = new Date(this.fechaSemanaActual());
    fecha.setDate(fecha.getDate() - 7);
    this.fechaSemanaActual.set(fecha);
    void this.cargarCitasSemana();
  }

  irSemanaSiguiente(): void {
    const fecha = new Date(this.fechaSemanaActual());
    fecha.setDate(fecha.getDate() + 7);
    this.fechaSemanaActual.set(fecha);
    void this.cargarCitasSemana();
  }

  /** v2: vuelve a la semana actual (botón "Hoy"). */
  irSemanaHoy(): void {
    const hoy = new Date();
    this.fechaSemanaActual.set(hoy);
    this.anioSeleccionado.set(hoy.getFullYear());
    this.mesSeleccionado.set(hoy.getMonth() + 1);
    void this.cargarCitasSemana();
  }

  // === Calendario Mensual ===

  mesAnterior(): void {
    let mes = this.mesSeleccionado() - 1;
    let anio = this.anioSeleccionado();
    if (mes < 1) {
      mes = 12;
      anio--;
    }
    this.mesSeleccionado.set(mes);
    this.anioSeleccionado.set(anio);
  }

  mesSiguiente(): void {
    let mes = this.mesSeleccionado() + 1;
    let anio = this.anioSeleccionado();
    if (mes > 12) {
      mes = 1;
      anio++;
    }
    this.mesSeleccionado.set(mes);
    this.anioSeleccionado.set(anio);
  }

  onAnioChange(anio: number): void {
    // Solo procesar si no estamos inicializando y el valor realmente cambió
    if (this.isInitializing || anio === this.anioSeleccionado()) {
      return;
    }
    this.anioSeleccionado.set(anio);
    this.onMesAnioChange();
  }

  onMesChange(mes: number): void {
    // Solo procesar si no estamos inicializando y el valor realmente cambió
    if (this.isInitializing || mes === this.mesSeleccionado()) {
      return;
    }
    this.mesSeleccionado.set(mes);
    this.onMesAnioChange();
  }

  onMesAnioChange(): void {
    // Si estamos inicializando o no hay datos de asesor, no hacer nada
    if (this.isInitializing || (!this.asesorDni() && !this.asesorNmconsec())) {
      return;
    }
    // Actualizar la semana seleccionada al primer día del mes
    const primerDia = new Date(
      this.anioSeleccionado(),
      this.mesSeleccionado() - 1,
      1
    );
    this.fechaSemanaActual.set(primerDia);
    void this.cargarCitasSemana();
  }

  seleccionarSemana(dia: number): void {
    const fecha = new Date(
      this.anioSeleccionado(),
      this.mesSeleccionado() - 1,
      dia
    );
    this.fechaSemanaActual.set(fecha);
    void this.cargarCitasSemana();
  }

  esDiaEnSemanaSeleccionada(dia: number): boolean {
    const citas = this.citasSemana();
    if (citas.length === 0) return false;

    const fecha = new Date(
      this.anioSeleccionado(),
      this.mesSeleccionado() - 1,
      dia
    );
    // Usar formato local YYYY-MM-DD (no toISOString que convierte a UTC)
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const dayStr = String(fecha.getDate()).padStart(2, '0');
    const fechaStr = `${year}-${month}-${dayStr}`;

    // Extraer solo la fecha (YYYY-MM-DD) de feprogramacion (ISO datetime)
    return citas.some(
      (cita) => cita.feprogramacion.substring(0, 10) === fechaStr
    );
  }

  esHoy(dia: number): boolean {
    const hoy = new Date();
    return (
      dia === hoy.getDate() &&
      this.mesSeleccionado() === hoy.getMonth() + 1 &&
      this.anioSeleccionado() === hoy.getFullYear()
    );
  }

  // === Eventos del calendario ===

  /**
   * Al hacer click en una celda vacía, muestra el selector de tipo de cita.
   * Como venimos con información de una Orden, preguntamos si la cita será:
   * - Administrativas: sin vínculo a orden (tipos ADMIN)
   * - Campo/Informe: vinculada a la orden actual (tipos SERVICIO)
   */
  onCeldaClick(event: { fecha: string; hora: number; minuto?: number }): void {
    console.log('[AgendaAsesor] Celda clickeada:', event);

    // Guardar datos de la celda para usarlos después de la selección
    this.celdaClickPendiente.set(event);
    this.mostrarSelectorTipoCita.set(true);
  }

  /**
   * Handler cuando el usuario selecciona el tipo de cita (Administrativas o Campo/Informe)
   */
  async onTipoCitaSeleccionado(usoCita: UsoCitaTipo): Promise<void> {
    console.log('[AgendaAsesor] Tipo de cita seleccionado:', usoCita);

    this.mostrarSelectorTipoCita.set(false);
    this.tipoCitaSeleccionadoParaCrear.set(usoCita);

    const event = this.celdaClickPendiente();
    if (!event) {
      console.warn('[AgendaAsesor] No hay evento de celda pendiente');
      return;
    }

    // Cargar tipos de cita según la selección
    try {
      let tipos = await this.agendaRepository.listarTiposCita(
        this.dniAsesor(),
        usoCita
      );

      // Si es SERVICIO y la clasificación es CAPACITACIÓN, solo mostrar tipo CAMPO (nmconsecutivo = 17)
      // Legacy: ServiceAppointmentActivity.loadCapacitacion() - if ("3".equals(idClass)) parms = {"NMCONSECUTIVO", "17"}
      if (usoCita === 'SERVICIO') {
        const clasificacion =
          this.ordenServicioDetalle()?.clasificacion?.toUpperCase() || '';
        const esCapacitacion = clasificacion.includes('CAPACIT');

        if (esCapacitacion) {
          console.log(
            '[AgendaAsesor] Clasificación CAPACITACIÓN detectada, filtrando solo tipo CAMPO (17)'
          );
          tipos = tipos.filter((t) => t.nmconsecutivo === 17);
        }
      }

      this.tiposCita.set(tipos);
      console.log(
        `[AgendaAsesor] Tipos de cita ${usoCita} cargados:`,
        tipos.length
      );
    } catch (error) {
      console.error('[AgendaAsesor] Error cargando tipos de cita:', error);
    }

    // Crear cita nueva con fecha/hora seleccionada
    // Parsear fecha local YYYY-MM-DD correctamente (no usar new Date(string) que puede interpretar como UTC)
    const [year, month, day] = event.fecha.split('-').map(Number);
    const fechaHora = new Date(
      year,
      month - 1,
      day,
      event.hora,
      event.minuto ?? 0,
      0,
      0
    );

    // Para citas de SERVICIO, incluir la orden de servicio detalle
    const ordenDetalle = this.ordenServicioDetalle();

    const citaParcial: Partial<ProgramacionServicio> = {
      feprogramacion: fechaHora.toISOString(),
      dniAsesor: this.dniAsesor(),
      nmconsecutivoAsesor: this.nmconsecutivoAsesor() ?? undefined,
      nmhorasCita: 1,
      // Para citas de SERVICIO (CAMPO/INFORME), asociar con la orden de servicio
      nmconsecOrdenServDetalle:
        usoCita === 'SERVICIO' ? ordenDetalle?.nmconsecutivo : undefined
    };

    this.citaNueva.set(citaParcial as ProgramacionServicio);
    this.mostrarCrearCita.set(true);

    // Limpiar el evento pendiente
    this.celdaClickPendiente.set(null);
  }

  /**
   * Handler cuando el usuario cancela la selección de tipo de cita
   */
  onTipoCitaCancelado(): void {
    console.log('[AgendaAsesor] Selección de tipo de cita cancelada');
    this.mostrarSelectorTipoCita.set(false);
    this.celdaClickPendiente.set(null);
  }

  async onCitaClick(cita: ProgramacionServicio): Promise<void> {
    console.log('[AgendaAsesor] Cita clickeada:', cita.nmconsecutivoProg);

    // Determinar modo según estado: edit para citas PROGRAMADAS o REPROGRAMADAS, view para otras
    const estadoCita = cita.cdestado?.toUpperCase() || '';
    const esEditable =
      estadoCita === ESTADO_CITA.PROGRAMADA ||
      estadoCita === ESTADO_CITA.REPROGRAMADA ||
      estadoCita === ESTADO_CITA.PRG ||
      estadoCita === ESTADO_CITA.REP;
    this.modoCita.set(esEditable ? 'edit' : 'view');
    console.log(
      '[AgendaAsesor] Modo cita:',
      this.modoCita(),
      '- Estado:',
      estadoCita
    );

    try {
      let citaParaModal = cita;

      // Cargar el detalle completo de la cita (incluye datos de contacto y orden de servicio)
      if (cita.nmconsecutivoProg) {
        const detalle = await this.agendaRepository.obtenerCita(
          cita.nmconsecutivoProg
        );
        console.log(
          '[AgendaAsesor] Detalle cita cargado, nmconsecutivoProg:',
          cita.nmconsecutivoProg
        );
        citaParaModal = detalle;

        // Re-evaluar modo con el detalle (puede tener estado más actualizado)
        const estadoDetalle = detalle.cdestado?.toUpperCase() || '';
        const esEditableDetalle =
          estadoDetalle === ESTADO_CITA.PROGRAMADA ||
          estadoDetalle === ESTADO_CITA.REPROGRAMADA ||
          estadoDetalle === ESTADO_CITA.PRG ||
          estadoDetalle === ESTADO_CITA.REP;
        this.modoCita.set(esEditableDetalle ? 'edit' : 'view');
      }

      this.citaSeleccionada.set(citaParaModal);
      this.mostrarVerCita.set(true);
    } catch (error) {
      console.error('[AgendaAsesor] Error cargando detalle de cita:', error);
      // Fallback: abrir con la cita resumida si falla el detalle
      this.citaSeleccionada.set(cita);
      this.mostrarVerCita.set(true);
    }
  }

  // === Diálogo crear cita ===

  cerrarCrearCita(): void {
    this.mostrarCrearCita.set(false);
    this.citaNueva.set(null);
    this.tipoCitaSeleccionadoParaCrear.set(null);
    // Refrescar agenda al cerrar
    void this.cargarCitasSemana();
  }

  async onGuardarCita(request: ProgramacionServicioRequest): Promise<void> {
    try {
      console.log(
        '[AgendaAsesor] Guardando cita, nmconsecOrdenServDetalle:',
        request.nmconsecOrdenServDetalle
      );

      // Agregar DNI del asesor si no viene
      if (!request.dniAsesor) {
        request.dniAsesor = this.dniAsesor();
      }
      if (!request.nmconsecutivoAsesor && this.nmconsecutivoAsesor()) {
        request.nmconsecutivoAsesor = this.nmconsecutivoAsesor()!;
      }

      // Cuando se crea desde OC, enviar snCheckProgramacion='S'
      if (this.ordenServicioDetalle()) {
        request.snCheckProgramacion = 'S';
      }

      const citaCreada = await this.agendaRepository.crearCita(request);

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Cita creada correctamente'
      });

      // Cerrar diálogo de crear y recargar citas
      this.cerrarCrearCita();
      await this.cargarCitasSemana();

      // Emitir evento para que el padre recargue las programaciones
      this.citaCreada.emit(citaCreada);
    } catch (error: unknown) {
      console.error('[AgendaAsesor] Error guardando cita:', error);

      const errorMsg = extractHttpErrorMessage(error, 'No se pudo crear la cita');

      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMsg
      });
    }
  }

  // === Diálogo ver/editar cita ===

  cerrarVerCita(): void {
    console.log('[AgendaAsesor] cerrarVerCita() llamado');
    this.mostrarVerCita.set(false);
    this.citaSeleccionada.set(null);
    // Refrescar agenda al cerrar
    console.log(
      '[AgendaAsesor] Llamando cargarCitasSemana desde cerrarVerCita...'
    );
    void this.cargarCitasSemana();
  }

  async onActualizarCita(request: ProgramacionServicioRequest): Promise<void> {
    const cita = this.citaSeleccionada();
    if (!cita?.nmconsecutivoProg) return;

    try {
      await this.agendaRepository.actualizarCita(
        cita.nmconsecutivoProg,
        request
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Cita actualizada correctamente'
      });

      this.cerrarVerCita();
      await this.cargarCitasSemana();
    } catch (error: unknown) {
      console.error('[AgendaAsesor] Error actualizando cita:', error);
      const errorMsg =
        error instanceof Error
          ? error.message
          : 'No se pudo actualizar la cita';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMsg
      });
    }
  }

  /**
   * Handler para cancelar cita desde el diálogo de cita
   */
  async onCancelarCita(cancelacion: CancelarCitaResult): Promise<void> {
    console.log('[AgendaAsesor] onCancelarCita - citaId:', cancelacion.citaId);
    try {
      await this.agendaRepository.cancelarCitaSP(cancelacion);

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Cita cancelada correctamente'
      });

      this.cerrarVerCita();
      await this.cargarCitasSemana();
    } catch (error: unknown) {
      console.error('[AgendaAsesor] Error cancelando cita:', error);
      const errorMsg =
        error instanceof Error ? error.message : 'No se pudo cancelar la cita';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMsg
      });
    }
  }

  /**
   * Handler para cumplir cita desde el diálogo de cita
   */
  onCumplirCita(citaId: number): void {
    console.log('[AgendaAsesor] onCumplirCita - citaId:', citaId);
    // TODO: Implementar lógica de cumplimiento
    this.messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: 'Funcionalidad de cumplimiento en desarrollo'
    });
  }

  /**
   * Handler para cuando se registra un cumplimiento exitosamente desde el modal de cita.
   * Refresca las citas de la semana.
   *
   * Nota: El cierre del modal lo controla CitaDialogComponent.
   * Si el cumplimiento genera acta asociada, el modal debe permanecer abierto
   * para permitir la descarga inmediata.
   */
  async onCumplimientoRegistrado(citaId: number): Promise<void> {
    console.log('[AgendaAsesor] Cumplimiento registrado para cita:', citaId);
    // Delay para asegurar commit de SP_ACTUALIZAR_CITA en la BD
    await new Promise((resolve) => setTimeout(resolve, 500));
    // Refrescar las citas de la semana para mostrar el cambio de estado
    await this.cargarCitasSemana();
  }

  // === Cerrar diálogo principal ===

  cerrar(): void {
    this.dialogVisible = false;
    this.closed.emit();
  }

  private limpiar(): void {
    this.citasSemana.set([]);
    this.tiposCita.set([]);
    this.tiposCitaCompletos.set([]);
    this.mostrarCrearCita.set(false);
    this.mostrarVerCita.set(false);
    this.citaNueva.set(null);
    this.citaSeleccionada.set(null);
    // Limpiar valores de asesor capturados
    this.asesorDni.set('');
    this.asesorNmconsec.set(null);
    // Resetear fecha a la actual para próxima apertura
    const hoy = new Date();
    this.fechaSemanaActual.set(hoy);
    this.anioSeleccionado.set(hoy.getFullYear());
    this.mesSeleccionado.set(hoy.getMonth() + 1);
  }
}
